import { useMemo, useState } from 'react';
import { App, Form } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AssetCustomFieldType,
  type AssetCustomFieldType as AssetCustomFieldTypeValue,
} from '@evimo/types/enums/asset-custom-field-type';
import type { CreateAssetCustomFieldRequestDto } from '@evimo/types/api/asset-custom-field/create-asset-custom-field/create-asset-custom-field.request.dto';
import type { FindAssetCustomFieldResponseDto } from '@evimo/types/api/asset-custom-field/find-asset-custom-field/find-asset-custom-field.response.dto';
import type { UpdateAssetCustomFieldRequestDto } from '@evimo/types/api/asset-custom-field/update-asset-custom-field/update-asset-custom-field.request.dto';
import {
  DEFAULT_UNIT_GROUPS,
  DEFAULT_UNIT_VALUES,
  type AssetCustomFieldUnitOptionGroup,
} from '../../constants/asset-custom-field-units';
import { AssetCustomFieldService } from '~/app/services/asset-custom-field/asset-custom-field.service';
import { AssetCustomFieldFormValue } from '~/app/types/assetCustomFieldFormValue';

export function useAssetCustomFieldManagement(assetTypeId: string) {
  const { t } = useTranslation('assetCustomFields');
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const [fieldForm] = Form.useForm<AssetCustomFieldFormValue>();
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [customUnitOption, setCustomUnitOption] = useState<string | null>(null);
  const [editingField, setEditingField] =
    useState<FindAssetCustomFieldResponseDto | null>(null);

  const fieldTypeOptions = useMemo<
    Array<{
      value: AssetCustomFieldTypeValue;
      label: string;
    }>
  >(
    () => [
      {
        value: AssetCustomFieldType.TEXT,
        label: t('fieldTypes.text', 'Text'),
      },
      {
        value: AssetCustomFieldType.NUMBER,
        label: t('fieldTypes.number', 'Number'),
      },
      {
        value: AssetCustomFieldType.DATE,
        label: t('fieldTypes.date', 'Date'),
      },
      {
        value: AssetCustomFieldType.BOOLEAN,
        label: t('fieldTypes.boolean', 'Boolean'),
      },
    ],
    [t],
  );

  const unitOptions = useMemo<AssetCustomFieldUnitOptionGroup[]>(() => {
    const options: AssetCustomFieldUnitOptionGroup[] = DEFAULT_UNIT_GROUPS.map(
      (group) => ({
        label: group.label,
        options: [...group.options],
      }),
    );

    if (customUnitOption && !DEFAULT_UNIT_VALUES.includes(customUnitOption)) {
      options.push({
        label: t('form.existingUnitGroupLabel', 'Existing'),
        options: [
          {
            value: customUnitOption,
            label: `${customUnitOption} (${t('form.existingUnitLabel', 'existing')})`,
          },
        ],
      });
    }

    return options;
  }, [customUnitOption, t]);

  const {
    data: fields = [],
    isLoading: isLoadingFields,
    error: fieldsError,
  } = useQuery({
    queryKey: ['asset-custom-fields', assetTypeId],
    queryFn: () => AssetCustomFieldService.findByAssetType(assetTypeId),
    enabled: Boolean(assetTypeId),
  });

  const createFieldMutation = useMutation({
    mutationFn: (dto: Omit<CreateAssetCustomFieldRequestDto, 'assetTypeId'>) =>
      AssetCustomFieldService.create(assetTypeId, dto),
    onSuccess: async () => {
      message.success(
        t('messages.fieldCreated', 'Custom field created successfully'),
      );
      setFieldModalOpen(false);
      setEditingField(null);
      fieldForm.resetFields();
      await queryClient.invalidateQueries({
        queryKey: ['asset-custom-fields', assetTypeId],
      });
    },
    onError: () => {
      message.error('Failed to create custom field');
    },
  });

  const updateFieldMutation = useMutation({
    mutationFn: (payload: {
      id: string;
      dto: UpdateAssetCustomFieldRequestDto;
    }) => AssetCustomFieldService.update(assetTypeId, payload.id, payload.dto),
    onSuccess: async () => {
      message.success(
        t('messages.fieldUpdated', 'Custom field updated successfully'),
      );
      setFieldModalOpen(false);
      setEditingField(null);
      fieldForm.resetFields();
      await queryClient.invalidateQueries({
        queryKey: ['asset-custom-fields', assetTypeId],
      });
    },
    onError: () => {
      message.error('Failed to update custom field');
    },
  });

  const deleteFieldMutation = useMutation({
    mutationFn: (id: string) => AssetCustomFieldService.delete(assetTypeId, id),
    onSuccess: async () => {
      message.success(
        t('messages.fieldDeleted', 'Custom field deleted successfully'),
      );
      await queryClient.invalidateQueries({
        queryKey: ['asset-custom-fields', assetTypeId],
      });
    },
    onError: () => {
      message.error('Failed to delete custom field');
    },
  });

  const parseRulesText = (
    rulesText?: string,
  ): Record<string, unknown> | null => {
    const trimmed = rulesText?.trim();
    if (!trimmed) {
      return null;
    }

    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (
        parsed === null ||
        Array.isArray(parsed) ||
        typeof parsed !== 'object'
      ) {
        throw new Error('rules-must-be-object');
      }
      return parsed as Record<string, unknown>;
    } catch {
      throw new Error(
        t('validation.rulesInvalid', 'Rules must be a valid JSON object'),
      );
    }
  };

  const buildRules = (params: {
    rulesText?: string;
    unit?: string;
    fieldType: AssetCustomFieldTypeValue;
  }): Record<string, unknown> | null => {
    const rules = parseRulesText(params.rulesText) ?? {};

    if (params.fieldType === AssetCustomFieldType.NUMBER) {
      const normalizedUnit = params.unit?.trim();
      if (normalizedUnit) {
        rules.unit = normalizedUnit;
      } else {
        delete rules.unit;
      }
    } else {
      delete rules.unit;
    }

    return Object.keys(rules).length > 0 ? rules : null;
  };

  const openCreateField = () => {
    setEditingField(null);
    setCustomUnitOption(null);
    fieldForm.resetFields();
    fieldForm.setFieldsValue({
      fieldType: AssetCustomFieldType.TEXT,
      required: false,
      unit: undefined,
    });
    setFieldModalOpen(true);
  };

  const openEditField = (field: FindAssetCustomFieldResponseDto) => {
    const existingRules =
      field.rules &&
      typeof field.rules === 'object' &&
      !Array.isArray(field.rules)
        ? (field.rules as Record<string, unknown>)
        : {};
    const existingUnit =
      typeof existingRules.unit === 'string' ? existingRules.unit.trim() : '';

    setCustomUnitOption(
      existingUnit && !DEFAULT_UNIT_VALUES.includes(existingUnit)
        ? existingUnit
        : null,
    );

    setEditingField(field);
    fieldForm.setFieldsValue({
      name: field.name,
      fieldKey: field.fieldKey,
      fieldType: field.fieldType,
      required: field.required,
      unit: existingUnit || undefined,
      rulesText: field.rules ? JSON.stringify(field.rules, null, 2) : '',
    });
    setFieldModalOpen(true);
  };

  const closeFieldModal = () => {
    setFieldModalOpen(false);
    setEditingField(null);
    setCustomUnitOption(null);
    fieldForm.resetFields();
  };

  const confirmDeleteField = (field: FindAssetCustomFieldResponseDto) => {
    modal.confirm({
      title: t('confirmDeleteField.title', 'Delete custom field?'),
      content: t(
        'confirmDeleteField.description',
        'This action will also remove all values attached to this field.',
      ),
      okText: t('actions.delete', 'Delete'),
      cancelText: t('actions.cancel', 'Cancel'),
      okButtonProps: { danger: true },
      onOk: () => deleteFieldMutation.mutate(field.id),
    });
  };

  const submitFieldForm = async () => {
    try {
      const values = await fieldForm.validateFields();
      const fieldType = editingField?.fieldType ?? values.fieldType;
      const rules = buildRules({
        fieldType,
        rulesText: values.rulesText,
        unit: values.unit,
      });

      if (editingField) {
        updateFieldMutation.mutate({
          id: editingField.id,
          dto: {
            name: values.name.trim(),
            required: values.required,
            rules,
          },
        });
      } else {
        createFieldMutation.mutate({
          name: values.name.trim(),
          fieldKey: values.fieldKey.trim(),
          fieldType: values.fieldType,
          required: values.required,
          rules,
        });
      }
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      }
    }
  };

  return {
    fields,
    isLoadingFields,
    fieldsError,
    fieldForm,
    fieldModalOpen,
    editingField,
    fieldTypeOptions: fieldTypeOptions as Array<{
      value: AssetCustomFieldTypeValue;
      label: string;
    }>,
    unitOptions,
    isSubmitting:
      createFieldMutation.isPending || updateFieldMutation.isPending,
    openCreateField,
    openEditField,
    closeFieldModal,
    confirmDeleteField,
    submitFieldForm,
  };
}
